// src/hooks/useModuleI18n.ts
// Hook personalizado para internacionalización de módulos

import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import {
  getModuleI18nConfig,
  getDomainI18nConfig,
  getModuleById,
  getModuleGroup,
  DOMAIN_TO_NAMESPACE,
  type ModuleI18nConfig,
  type DomainI18nConfig,
} from "../convex/lib/config";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Opciones para el hook useModuleI18n
 */
export interface UseModuleI18nOptions {
  /** Si debe cargar el namespace automáticamente */
  loadNamespace?: boolean;
}

/**
 * Traducciones de acciones del módulo
 */
export interface ModuleActions {
  create: string;
  edit: string;
  delete: string;
  view: string;
  export: string;
  import: string;
  duplicate: string;
  archive: string;
  restore: string;
}

/**
 * Resultado del hook useModuleI18n
 */
export interface UseModuleI18nResult {
  /** Título traducido del módulo */
  title: string;
  /** Descripción traducida del módulo */
  description: string;
  /** Descripción larga traducida */
  longDescription: string;
  /** Placeholder de búsqueda traducido */
  searchPlaceholder: string;
  /** Mensaje de estado vacío traducido */
  emptyState: string;
  /** Mensaje de error traducido */
  errorState: string;
  /** Acciones traducidas */
  actions: ModuleActions;

  /** Traduce un valor de enum (ej: status) */
  translateEnum: (field: string, value: string) => string;
  /** Traduce un nombre de campo */
  translateField: (field: string) => string;
  /** Traduce un mensaje del módulo */
  translateMessage: (message: string) => string;
  /** Traduce un tooltip del módulo */
  translateTooltip: (key: string) => string;
  /** Función t genérica para el namespace del módulo */
  t: (key: string, options?: Record<string, unknown>) => string;

  /** Namespace del módulo */
  namespace: string;
  /** Si las traducciones están cargadas */
  ready: boolean;
  /** Idioma actual */
  language: string;
}

/**
 * Resultado del hook useDomainI18n
 */
export interface UseDomainI18nResult {
  /** Título traducido del dominio */
  title: string;
  /** Descripción traducida del dominio */
  description: string;
  /** Función t genérica para el namespace del dominio */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Namespace del dominio */
  namespace: string;
  /** Si las traducciones están cargadas */
  ready: boolean;
}

/**
 * Resultado del hook useCommonI18n
 */
export interface UseCommonI18nResult {
  /** Traducciones de acciones comunes */
  actions: {
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    create: string;
    add: string;
    remove: string;
    search: string;
    filter: string;
    export: string;
    import: string;
    download: string;
    upload: string;
    refresh: string;
    back: string;
    next: string;
    previous: string;
    confirm: string;
    close: string;
  };

  /** Traducciones de estados comunes */
  status: {
    active: string;
    inactive: string;
    pending: string;
    completed: string;
    cancelled: string;
    draft: string;
    archived: string;
    deleted: string;
    processing: string;
    error: string;
    success: string;
  };

  /** Traducciones de mensajes comunes */
  messages: {
    loading: string;
    saving: string;
    deleting: string;
    noResults: string;
    noData: string;
    error: string;
    success: string;
    confirmDelete: string;
    unsavedChanges: string;
  };

  /** Traducciones de labels comunes */
  labels: {
    name: string;
    description: string;
    email: string;
    phone: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    total: string;
    amount: string;
    quantity: string;
  };

  /** Traducciones para LocationField */
  location: {
    country: string;
    selectCountry: string;
    searchCountry: string;
    countryNotFound: string;
    department: string;
    selectDepartment: string;
    searchDepartment: string;
    departmentNotFound: string;
    municipality: string;
    selectMunicipality: string;
    selectDepartmentFirst: string;
    searchMunicipality: string;
    municipalityNotFound: string;
    state: string;
    selectState: string;
    searchState: string;
    stateNotFound: string;
    city: string;
    cityPlaceholder: string;
    zipCode: string;
    zipCodePlaceholder: string;
    zipCodeUS: string;
    zipCodeUSPlaceholder: string;
    address: string;
    addressPlaceholder: string;
    mapUrl: string;
    mapUrlPlaceholder: string;
    openInMaps: string;
  };

  /** Funcion t generica para common */
  t: (key: string, options?: Record<string, unknown>) => string;

  /** Si las traducciones están cargadas */
  ready: boolean;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook para obtener traducciones de un módulo específico
 *
 * @example
 * ```tsx
 * function OrdersList() {
 *   const {
 *     title,
 *     actions,
 *     translateEnum,
 *     translateField
 *   } = useModuleI18n("logistics_orders");
 *
 *   return (
 *     <div>
 *       <h1>{title}</h1>
 *       <Button>{actions.create}</Button>
 *       <span>{translateField("orderNumber")}</span>
 *       <Badge>{translateEnum("status", order.status)}</Badge>
 *     </div>
 *   );
 * }
 * ```
 */
export function useModuleI18n(
  moduleId: string,
  options: UseModuleI18nOptions = {},
): UseModuleI18nResult {
  // Obtener configuración del módulo
  const moduleConfig = useMemo(() => getModuleI18nConfig(moduleId), [moduleId]);
  const module = useMemo(() => getModuleById(moduleId), [moduleId]);
  const domain = useMemo(() => getModuleGroup(moduleId), [moduleId]);

  // Determinar namespace
  const domainNamespace =
    domain?.i18n?.namespace ??
    DOMAIN_TO_NAMESPACE[domain?.id ?? ""] ??
    domain?.id ??
    "common";

  const namespace = moduleConfig?.namespace.split(".")[0] ?? domainNamespace;

  // Hook de traducción
  const { t, ready, i18n } = useTranslation(namespace);

  // Prefijo para las keys del módulo
  const modulePrefix =
    moduleConfig?.namespace ?? `${domainNamespace}.${moduleId}`;
  const keyPrefix = modulePrefix.includes(".")
    ? modulePrefix.split(".").slice(1).join(".")
    : moduleId;

  // Función helper para traducir con fallback
  const translate = useCallback(
    (key: string, fallback?: string) => {
      const fullKey = `${keyPrefix}.${key}`;
      const result = t(fullKey, { defaultValue: fallback ?? key });
      return result;
    },
    [t, keyPrefix],
  );

  // Traducciones básicas
  const title = useMemo(
    () => translate("title", module?.name),
    [translate, module?.name],
  );

  const description = useMemo(
    () => translate("description", module?.description),
    [translate, module?.description],
  );

  const longDescription = useMemo(
    () => translate("longDescription", ""),
    [translate],
  );

  const searchPlaceholder = useMemo(
    () => translate("searchPlaceholder", "Buscar..."),
    [translate],
  );

  const emptyState = useMemo(
    () => translate("emptyState", "No hay registros"),
    [translate],
  );

  const errorState = useMemo(
    () => translate("errorState", "Ha ocurrido un error"),
    [translate],
  );

  // Acciones traducidas
  const actions = useMemo<ModuleActions>(
    () => ({
      create: translate("actions.create", "Crear"),
      edit: translate("actions.edit", "Editar"),
      delete: translate("actions.delete", "Eliminar"),
      view: translate("actions.view", "Ver"),
      export: translate("actions.export", "Exportar"),
      import: translate("actions.import", "Importar"),
      duplicate: translate("actions.duplicate", "Duplicar"),
      archive: translate("actions.archive", "Archivar"),
      restore: translate("actions.restore", "Restaurar"),
    }),
    [translate],
  );

  // Función para traducir enums
  const translateEnum = useCallback(
    (field: string, value: string): string => {
      const key = `${keyPrefix}.${field}.${value}`;
      return t(key, { defaultValue: value });
    },
    [t, keyPrefix],
  );

  // Función para traducir campos
  const translateField = useCallback(
    (field: string): string => {
      const key = `${keyPrefix}.fields.${field}`;
      return t(key, { defaultValue: field });
    },
    [t, keyPrefix],
  );

  // Función para traducir mensajes
  const translateMessage = useCallback(
    (message: string): string => {
      const key = `${keyPrefix}.messages.${message}`;
      return t(key, { defaultValue: message });
    },
    [t, keyPrefix],
  );

  // Función para traducir tooltips
  const translateTooltip = useCallback(
    (tooltip: string): string => {
      const key = `${keyPrefix}.tooltips.${tooltip}`;
      return t(key, { defaultValue: "" });
    },
    [t, keyPrefix],
  );

  // Función t genérica para el módulo
  const moduleT = useCallback(
    (key: string, opts?: Record<string, unknown>): string => {
      // Si la key ya tiene el prefijo, usarla directamente
      if (key.startsWith(keyPrefix)) {
        return t(key, opts);
      }
      return t(`${keyPrefix}.${key}`, opts);
    },
    [t, keyPrefix],
  );

  return {
    title,
    description,
    longDescription,
    searchPlaceholder,
    emptyState,
    errorState,
    actions,
    translateEnum,
    translateField,
    translateMessage,
    translateTooltip,
    t: moduleT,
    namespace,
    ready,
    language: i18n.language,
  };
}

/**
 * Hook para obtener traducciones de un dominio
 *
 * @example
 * ```tsx
 * function LogisticsPage() {
 *   const { title, description } = useDomainI18n("logistics");
 *
 *   return (
 *     <div>
 *       <h1>{title}</h1>
 *       <p>{description}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useDomainI18n(domainId: string): UseDomainI18nResult {
  const domainConfig = useMemo(() => getDomainI18nConfig(domainId), [domainId]);
  const namespace = domainConfig.namespace;

  const { t, ready } = useTranslation(namespace);

  const title = useMemo(
    () => t("title", { defaultValue: domainId }),
    [t, domainId],
  );

  const description = useMemo(
    () => t("description", { defaultValue: "" }),
    [t],
  );

  const domainT = useCallback(
    (key: string, opts?: Record<string, unknown>): string => {
      return t(key, opts);
    },
    [t],
  );

  return {
    title,
    description,
    t: domainT,
    namespace,
    ready,
  };
}

/**
 * Hook para obtener traducciones comunes
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { actions, messages, labels } = useCommonI18n();
 *
 *   return (
 *     <div>
 *       <Button onClick={handleSave}>{actions.save}</Button>
 *       <Button onClick={handleCancel}>{actions.cancel}</Button>
 *       {isLoading && <span>{messages.loading}</span>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useCommonI18n(): UseCommonI18nResult {
  const { t: i18nT, ready } = useTranslation("common");

  /**
   * Wrapper seguro que siempre retorna string
   */
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const result = i18nT(key, options);
      return typeof result === "string" ? result : String(result);
    },
    [i18nT],
  );

  const actions = useMemo(
    () => ({
      save: t("actions.save", { defaultValue: "Guardar" }),
      cancel: t("actions.cancel", { defaultValue: "Cancelar" }),
      delete: t("actions.delete", { defaultValue: "Eliminar" }),
      edit: t("actions.edit", { defaultValue: "Editar" }),
      create: t("actions.create", { defaultValue: "Crear" }),
      add: t("actions.add", { defaultValue: "Agregar" }),
      remove: t("actions.remove", { defaultValue: "Remover" }),
      search: t("actions.search", { defaultValue: "Buscar" }),
      filter: t("actions.filter", { defaultValue: "Filtrar" }),
      export: t("actions.export", { defaultValue: "Exportar" }),
      import: t("actions.import", { defaultValue: "Importar" }),
      download: t("actions.download", { defaultValue: "Descargar" }),
      upload: t("actions.upload", { defaultValue: "Subir" }),
      refresh: t("actions.refresh", { defaultValue: "Actualizar" }),
      back: t("actions.back", { defaultValue: "Volver" }),
      next: t("actions.next", { defaultValue: "Siguiente" }),
      previous: t("actions.previous", { defaultValue: "Anterior" }),
      confirm: t("actions.confirm", { defaultValue: "Confirmar" }),
      close: t("actions.close", { defaultValue: "Cerrar" }),
    }),
    [t],
  );

  const status = useMemo(
    () => ({
      active: t("status.active", { defaultValue: "Activo" }),
      inactive: t("status.inactive", { defaultValue: "Inactivo" }),
      pending: t("status.pending", { defaultValue: "Pendiente" }),
      completed: t("status.completed", { defaultValue: "Completado" }),
      cancelled: t("status.cancelled", { defaultValue: "Cancelado" }),
      draft: t("status.draft", { defaultValue: "Borrador" }),
      archived: t("status.archived", { defaultValue: "Archivado" }),
      deleted: t("status.deleted", { defaultValue: "Eliminado" }),
      processing: t("status.processing", { defaultValue: "Procesando" }),
      error: t("status.error", { defaultValue: "Error" }),
      success: t("status.success", { defaultValue: "Éxito" }),
    }),
    [t],
  );

  const messages = useMemo(
    () => ({
      loading: t("messages.loading", { defaultValue: "Cargando..." }),
      saving: t("messages.saving", { defaultValue: "Guardando..." }),
      deleting: t("messages.deleting", { defaultValue: "Eliminando..." }),
      noResults: t("messages.noResults", { defaultValue: "Sin resultados" }),
      noData: t("messages.noData", { defaultValue: "Sin datos" }),
      error: t("messages.error", { defaultValue: "Ha ocurrido un error" }),
      success: t("messages.success", { defaultValue: "Operación exitosa" }),
      confirmDelete: t("messages.confirmDelete", {
        defaultValue: "¿Estás seguro de que deseas eliminar este elemento?",
      }),
      unsavedChanges: t("messages.unsavedChanges", {
        defaultValue: "Tienes cambios sin guardar. ¿Deseas continuar?",
      }),
    }),
    [t],
  );

  const labels = useMemo(
    () => ({
      name: t("labels.name", { defaultValue: "Nombre" }),
      description: t("labels.description", { defaultValue: "Descripción" }),
      email: t("labels.email", { defaultValue: "Correo Electrónico" }),
      phone: t("labels.phone", { defaultValue: "Teléfono" }),
      status: t("labels.status", { defaultValue: "Estado" }),
      createdAt: t("labels.createdAt", { defaultValue: "Creado" }),
      updatedAt: t("labels.updatedAt", { defaultValue: "Actualizado" }),
      total: t("labels.total", { defaultValue: "Total" }),
      amount: t("labels.amount", { defaultValue: "Monto" }),
      quantity: t("labels.quantity", { defaultValue: "Cantidad" }),
    }),
    [t],
  );

  const location = useMemo(
    () => ({
      country: t("location.country", { defaultValue: "País" }),
      selectCountry: t("location.selectCountry", { defaultValue: "Selecciona un país" }),
      searchCountry: t("location.searchCountry", { defaultValue: "Buscar país..." }),
      countryNotFound: t("location.countryNotFound", { defaultValue: "País no encontrado" }),
      department: t("location.department", { defaultValue: "Departamento" }),
      selectDepartment: t("location.selectDepartment", { defaultValue: "Selecciona un departamento" }),
      searchDepartment: t("location.searchDepartment", { defaultValue: "Buscar departamento..." }),
      departmentNotFound: t("location.departmentNotFound", { defaultValue: "Departamento no encontrado" }),
      municipality: t("location.municipality", { defaultValue: "Municipio" }),
      selectMunicipality: t("location.selectMunicipality", { defaultValue: "Selecciona un municipio" }),
      selectDepartmentFirst: t("location.selectDepartmentFirst", { defaultValue: "Selecciona primero un departamento" }),
      searchMunicipality: t("location.searchMunicipality", { defaultValue: "Buscar municipio..." }),
      municipalityNotFound: t("location.municipalityNotFound", { defaultValue: "Municipio no encontrado" }),
      state: t("location.state", { defaultValue: "Estado" }),
      selectState: t("location.selectState", { defaultValue: "Selecciona un estado" }),
      searchState: t("location.searchState", { defaultValue: "Buscar estado..." }),
      stateNotFound: t("location.stateNotFound", { defaultValue: "Estado no encontrado" }),
      city: t("location.city", { defaultValue: "Ciudad" }),
      cityPlaceholder: t("location.cityPlaceholder", { defaultValue: "Ingresa la ciudad" }),
      zipCode: t("location.zipCode", { defaultValue: "Código Postal" }),
      zipCodePlaceholder: t("location.zipCodePlaceholder", { defaultValue: "Ej: 00000" }),
      zipCodeUS: t("location.zipCodeUS", { defaultValue: "ZIP Code" }),
      zipCodeUSPlaceholder: t("location.zipCodeUSPlaceholder", { defaultValue: "Ej: 12345" }),
      address: t("location.address", { defaultValue: "Dirección" }),
      addressPlaceholder: t("location.addressPlaceholder", { defaultValue: "Ingresa la dirección completa" }),
      mapUrl: t("location.mapUrl", { defaultValue: "URL del Mapa" }),
      mapUrlPlaceholder: t("location.mapUrlPlaceholder", { defaultValue: "https://maps.google.com/..." }),
      openInMaps: t("location.openInMaps", { defaultValue: "Abrir en Mapas" }),
    }),
    [t],
  );

  return {
    actions,
    status,
    messages,
    labels,
    location,
    t,
    ready,
  };
}

/**
 * Hook para traducir un enum específico
 * Útil cuando solo necesitas traducir valores de un enum
 *
 * @example
 * ```tsx
 * function StatusBadge({ status }: { status: string }) {
 *   const translateStatus = useEnumI18n("logistics_orders", "status");
 *   return <Badge>{translateStatus(status)}</Badge>;
 * }
 * ```
 */
export function useEnumI18n(
  moduleId: string,
  enumField: string,
): (value: string) => string {
  const { translateEnum } = useModuleI18n(moduleId);

  return useCallback(
    (value: string) => translateEnum(enumField, value),
    [translateEnum, enumField],
  );
}

/**
 * Hook para traducir campos de un módulo
 * Útil para tablas y formularios
 *
 * @example
 * ```tsx
 * function OrdersTable() {
 *   const translateField = useFieldsI18n("logistics_orders");
 *
 *   const columns = [
 *     { header: translateField("orderNumber"), accessor: "orderNumber" },
 *     { header: translateField("sender"), accessor: "sender" },
 *     { header: translateField("status"), accessor: "status" },
 *   ];
 *
 *   return <Table columns={columns} data={orders} />;
 * }
 * ```
 */
export function useFieldsI18n(moduleId: string): (field: string) => string {
  const { translateField } = useModuleI18n(moduleId);
  return translateField;
}
